import { CopyButton } from 'components/button/CopyButton';
import { formatAddress } from 'utils/address';
import { formatDate } from 'utils/date';
import { useNetworkInfo } from 'views/hooks/useTransactionsDetails';
import { IDepositExtended } from 'views/types/typeTransaction';

interface CommonFieldsProps {
  transaction: IDepositExtended;
  isInternal?: boolean;
  hideNetwork?: boolean;
}
interface FeeInfo {
  type: string;
  amount: string;
  currency: string;
}

export const ClientInfoFields: React.FC<CommonFieldsProps> = ({
  transaction,
  isInternal,
  hideNetwork = false,
}) => {
  const networkInfo = useNetworkInfo(transaction);

  let clientId = isInternal
    ? (transaction?.sender?.coinsFlyId ?? '---')
    : (transaction?.user?.coins_fly_id ??
      transaction?.user?.coinsFlyId ??
      '---');

  if (transaction.type === 'internal') {
    const isPositive = +transaction.amountIn > 0;

    if (isPositive) {
      clientId = transaction?.receiver?.coins_fly_id || '---';
    } else {
      clientId = transaction?.user?.coins_fly_id || '---';
    }
  }

  return (
    <li className="flex flex-col">
      <div>
        Client ID: <span className="font-semibold ml-3">{clientId}</span>
      </div>
      {!hideNetwork && (
        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
          Network: <span className="font-medium">{networkInfo}</span>
        </div>
      )}
    </li>
  );
};

export const FeeBreakdownSection: React.FC<{
  feeInfo: FeeInfo[];
}> = ({ feeInfo }) => {
  if (feeInfo.length === 0) return null;

  return (
    <div className="ml-3 mt-2">
      <div className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">
        Fee Breakdown:
      </div>
      {feeInfo.map((fee, index) => (
        <div key={index} className="text-sm ml-2">
          {fee.type}:{' '}
          <span className="font-medium">
            {fee.amount} {fee.currency}
          </span>
        </div>
      ))}
    </div>
  );
};

export const TransactionHashSection: React.FC<{
  transaction: IDepositExtended;
  isInternal: boolean;
}> = ({ transaction, isInternal }) => {
  if (isInternal) return null;

  return (
    <>
      <li className="flex flex-col">
        <div className="flex items-center">
          Transaction hash:{' '}
          <span className="font-semibold ml-3">
            {transaction?.transactionInHash
              ? formatAddress(transaction.transactionInHash)
              : transaction?.transactionOutHash
                ? formatAddress(transaction.transactionOutHash)
                : transaction?.transactionHash
                  ? formatAddress(transaction.transactionHash)
                  : '---'}
          </span>
          {(transaction?.transactionInHash ||
            transaction?.transactionOutHash ||
            transaction?.transactionHash) && (
            <CopyButton
              value={
                transaction?.transactionInHash ||
                transaction?.transactionOutHash ||
                transaction?.transactionHash
              }
              className="ml-3"
            />
          )}
        </div>
      </li>
      {transaction?.refundTxHash && (
        <li className="flex flex-col">
          <div className="flex items-center">
            Refund transaction hash:{' '}
            <span className="font-semibold ml-3">
              {formatAddress(transaction.refundTxHash)}
            </span>
            <CopyButton value={transaction.refundTxHash} className="ml-3" />
          </div>
        </li>
      )}

      {transaction?.refundAddress && (
        <li className="flex flex-col">
          <div className="flex items-center">
            Refund address:{' '}
            <span className="font-semibold ml-3">
              {formatAddress(transaction?.refundAddress)}
            </span>
            <CopyButton value={transaction?.refundAddress} className="ml-3" />
          </div>
        </li>
      )}

      {transaction?.processedAt && (
        <li>
          Refunded at:{' '}
          <span className="font-semibold ml-3">
            {formatDate(transaction.processedAt, 'DD.MM.YYYY HH:mm')}
          </span>
        </li>
      )}

      {transaction?.processedByAdmin && (
        <li>
          Processed by:{' '}
          <span className="font-semibold ml-3">
            {transaction.processedByAdmin.login}
          </span>
        </li>
      )}
    </>
  );
};

export const AMLSection: React.FC<{
  transaction: IDepositExtended;
  isInternal: boolean;
}> = ({ transaction, isInternal }) => {
  if (isInternal) return null;

  return (
    <>
      <li>
        AML status:{' '}
        <span className="font-semibold ml-3 capitalize">
          {transaction.status === 'Rejected' &&
          transaction?.amlLog?.responseData?.result?.data?.status === 'success'
            ? 'Failed'
            : transaction?.amlLog?.responseData?.result?.data?.status || '---'}
        </span>
      </li>
      <li>
        Transaction AML score:{' '}
        <span className="font-semibold ml-3">
          {transaction?.amlLog?.responseData?.risk_score !== null &&
          transaction?.amlLog?.responseData?.risk_score !== undefined
            ? `${Math.round(transaction?.amlLog?.responseData?.risk_score * 100)}%`
            : '---'}
        </span>
      </li>
    </>
  );
};
